import { reportBunkered, reportConsumption, reportRob } from './fuel.js';
import type { VoyageReportKind } from './schemas.js';
import {
  type SummaryLeg,
  type SummaryPeriod,
  type SummaryReport,
  summarizeVoyage,
  VOYAGE_STATES,
  type VoyageState,
  type VoyageSummary,
} from './summary.js';

/**
 * Fuel & Consumption Analysis (aşama V6; README S21; ADR 0090). Seçilen raporlardan (sefer ya da
 * tarih aralığı) yakıt türüne ve duruma göre tüketim, günlük ortalama, mesafe başına tüketim,
 * dönem ve bacak dökümü, ROB akışı ve ikmalle mutabakat. Saklanmaz, türetilir (S12). Tüketim ve
 * durumlar `voyageSummary`'nin aynı yürüyüşünden (`summarizeVoyage`), satırlar `fuel.ts`'ten;
 * özet ve CP hesapları değişmez. Saf işlev; saat UTC.
 */

/**
 * ROB farkı eşiği (MT; ADR 0090 §4): beklenenden bu kadar sapma yuvarlama sayılır. Formlar ROB'u
 * bir ondalıkla önerir; daha büyük fark uyarıdır. En sıkı okuma: uyarı kayıt değiştirmez.
 */
export const ROB_TOLERANCE_MT = 0.1;

/**
 * ROB farkının türü: fazla (yazılmamış ikmal?), az (yazılmamış tüketim ya da kayıp) ya da tüketim
 * hiç yazılmamış ve ROB düşmüş (`consumption_missing`; düşüş tüketim olabilir, raporda boş).
 */
export type RobIssue = 'rob_higher' | 'rob_lower' | 'consumption_missing';

export interface FuelAnalysisReport extends SummaryReport {
  id?: string;
}

/** ROB akışının bir satırı: bir raporda bir yakıt. */
export interface RobFlowRow {
  reportId: string | null;
  kind: VoyageReportKind;
  reportedAt: string;
  fuel: string;
  /** Kapsamdaki ilk ROB: önceki yok, tüketimi önceki döneme ait. */
  opening: boolean;
  /** Bu yakıtın bir önceki ROB'u (kapsam içinde). */
  previousMt: number | null;
  /** Son rapordan beri yazılan tüketim; yazılmamışsa `null`. */
  consumptionMt: number | null;
  /** Noon'da yazılan ikmal. */
  bunkeredMt: number;
  /** Önceki − tüketim + ikmal. */
  expectedMt: number | null;
  robMt: number | null;
  /** ROB − beklenen: artı fazla (yazılmamış ikmal?), eksi az (yazılmamış tüketim ya da kayıp). */
  differenceMt: number | null;
  issue: RobIssue | null;
}

/** Kapsamın yakıt mutabakatı (bunker mutabakatı). */
export interface FuelReconciliation {
  openingMt: number | null;
  bunkeredMt: number;
  closingMt: number | null;
  /** Açılış + ikmal − kapanış: ROB'lara göre tüketim. */
  robConsumptionMt: number | null;
  /** Açılıştan sonra raporlarda yazılan tüketim. */
  reportedMt: number;
  /** Kapanış − (açılış − raporlanan + ikmal) = adım farklarının toplamı. */
  differenceMt: number | null;
  issue: RobIssue | null;
}

export interface FuelAnalysisFuel {
  fuel: string;
  totalMt: number;
  byState: Record<VoyageState, number>;
  /** Seyrin içinde boğaz ve kanal geçişindeki tüketim. */
  passageMt: number;
  /** Toplam / raporlu saat × 24. */
  dailyMt: number | null;
  dailyByState: Record<VoyageState, number | null>;
  /** Seyirdeki (bekleme dahil) tüketim / seyirdeki mesafe, kg/nm. */
  seaKgPerNm: number | null;
  reconciliation: FuelReconciliation;
}

export interface FuelAnalysisPeriod extends SummaryPeriod {
  fuels: Record<string, number>;
  totalMt: number | null;
  /** Tüketimi raporlanmış saat (son rapordan sonraki kalış hariç). */
  fuelHours: number;
  dailyMt: number | null;
}

export interface FuelAnalysisLeg extends SummaryLeg {
  fuels: Record<string, number>;
  totalMt: number | null;
  fuelHours: number;
  dailyMt: number | null;
  kgPerNm: number | null;
}

export interface FuelAnalysis {
  summary: VoyageSummary;
  /** Kapanış raporu olan aralıkların saatleri: günlük ortalamanın paydası. */
  fuelHours: Record<VoyageState | 'total', number>;
  fuels: FuelAnalysisFuel[];
  totalMt: number | null;
  dailyMt: number | null;
  seaKgPerNm: number | null;
  periods: FuelAnalysisPeriod[];
  legs: FuelAnalysisLeg[];
  robFlow: RobFlowRow[];
  /** Uyarılı ROB satırı sayısı. */
  issues: number;
}

/** Yuvarlama; kayan nokta artığından "−0" kalmaz (ekranda "−0.00" görünmesin). */
const round = (value: number, digits = 3) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor || 0;
};
const daily = (mt: number, hours: number) => (hours > 0 ? round((mt / hours) * 24) : null);
const perNm = (mt: number, nm: number) => (nm > 0 ? round((mt / nm) * 1000) : null);
const issueOf = (difference: number | null): RobIssue | null =>
  difference === null || Math.abs(difference) <= ROB_TOLERANCE_MT
    ? null
    : difference > 0
      ? 'rob_higher'
      : 'rob_lower';
const zeroStates = (): Record<VoyageState, number> => ({
  sea: 0,
  port: 0,
  anchorage: 0,
  drift: 0,
  waiting: 0,
});
const isSea = (state: VoyageState) => state === 'sea' || state === 'waiting';

interface FuelAcc {
  total: number;
  byState: Record<VoyageState, number>;
  passage: number;
  opening: number | null;
  closing: number | null;
  bunkered: number;
  reported: number;
}

interface SpanAcc {
  fuels: Map<string, number>;
  hours: number;
}

const addTo = (map: Map<string, number>, fuel: string, value: number) =>
  map.set(fuel, (map.get(fuel) ?? 0) + value);
const totalOf = (map: Map<string, number>) =>
  map.size > 0 ? round([...map.values()].reduce((sum, v) => sum + v, 0)) : null;
const roundMap = (map: Map<string, number>) =>
  Object.fromEntries([...map].map(([fuel, value]) => [fuel, round(value)]));

/**
 * Yakıt ve tüketim analizi. Aralık (`reports[i - 1]` → `reports[i]`) özetteki durumundadır;
 * tüketim kapanış raporundan, yakıt türüne göre. Günlük ortalama yalnız kapanış raporu olan
 * saatlerle: sefer bitişine uzayan son kalışın tüketimi henüz yazılmamıştır. ROB akışında her
 * raporun ROB'u bir önceki ROB'tan beklenenle karşılaştırılır.
 */
export function fuelAnalysis(
  reports: readonly FuelAnalysisReport[],
  options: { end: string | null },
): FuelAnalysis {
  const { summary, intervals } = summarizeVoyage(reports, options);
  const fuelHours = { ...zeroStates(), total: 0 };
  const fuels = new Map<string, FuelAcc>();
  const periods: SpanAcc[] = summary.periods.map(() => ({ fuels: new Map(), hours: 0 }));
  const legs: SpanAcc[] = summary.legs.map(() => ({ fuels: new Map(), hours: 0 }));
  const robFlow: RobFlowRow[] = [];
  const lastRob = new Map<string, number>();
  let seaDistance = 0;

  const fuelOf = (fuel: string): FuelAcc => {
    let acc = fuels.get(fuel);
    if (!acc) {
      acc = {
        total: 0,
        byState: zeroStates(),
        passage: 0,
        opening: null,
        closing: null,
        bunkered: 0,
        reported: 0,
      };
      fuels.set(fuel, acc);
    }
    return acc;
  };

  /** Raporun ROB satırları; `counted` değilse açılış (kapsamın ilk raporu). */
  const flow = (report: FuelAnalysisReport, counted: boolean) => {
    const consumption = new Map(counted ? reportConsumption(report) : []);
    const bunkered = counted ? reportBunkered(report) : new Map<string, number>();
    const robs = reportRob(report);
    const names = [...new Set([...robs.keys(), ...consumption.keys(), ...bunkered.keys()])];
    for (const fuel of names) {
      const acc = fuelOf(fuel);
      const previous = lastRob.get(fuel) ?? null;
      const used = consumption.get(fuel) ?? null;
      const bunker = bunkered.get(fuel) ?? 0;
      const robMt = robs.get(fuel) ?? null;
      const opening = previous === null;
      if (!opening) {
        acc.bunkered += bunker;
        acc.reported += used ?? 0;
      }
      const expectedMt = opening ? null : round(previous - (used ?? 0) + bunker);
      const differenceMt = expectedMt === null || robMt === null ? null : round(robMt - expectedMt);
      if (robMt !== null) {
        if (acc.opening === null) acc.opening = robMt;
        acc.closing = robMt;
        lastRob.set(fuel, robMt);
      }
      if (opening && robMt === null) continue;
      robFlow.push({
        reportId: report.id ?? null,
        kind: report.kind,
        reportedAt: report.reportedAt,
        fuel,
        opening,
        previousMt: previous,
        consumptionMt: opening ? null : used,
        bunkeredMt: opening ? 0 : bunker,
        expectedMt,
        robMt,
        differenceMt,
        issue:
          used === null && differenceMt !== null && differenceMt < -ROB_TOLERANCE_MT
            ? 'consumption_missing'
            : issueOf(differenceMt),
      });
    }
  };

  const first = intervals[0]?.previous;
  if (first) flow(first, false);
  for (const interval of intervals) {
    const { state, hours, report } = interval;
    fuelHours[state] += hours;
    if (isSea(state)) seaDistance += interval.distanceNm;
    const period = periods[interval.period] as SpanAcc;
    const leg = interval.leg === null ? null : (legs[interval.leg] as SpanAcc);
    period.hours += hours;
    if (leg) leg.hours += hours;
    for (const [fuel, value] of reportConsumption(report)) {
      const acc = fuelOf(fuel);
      acc.total += value;
      acc.byState[state] += value;
      if (interval.passage) acc.passage += value;
      addTo(period.fuels, fuel, value);
      if (leg) addTo(leg.fuels, fuel, value);
    }
    flow(report, true);
  }
  for (const key of VOYAGE_STATES) fuelHours[key] = round(fuelHours[key]);
  fuelHours.total = round(VOYAGE_STATES.reduce((sum, s) => sum + fuelHours[s], 0));

  const fuelRows: FuelAnalysisFuel[] = [...fuels].map(([fuel, acc]) => {
    const seaMt = acc.byState.sea + acc.byState.waiting;
    const differenceMt =
      acc.opening === null || acc.closing === null
        ? null
        : round(acc.closing - (acc.opening - acc.reported + acc.bunkered));
    return {
      fuel,
      totalMt: round(acc.total),
      byState: Object.fromEntries(VOYAGE_STATES.map((s) => [s, round(acc.byState[s])])) as Record<
        VoyageState,
        number
      >,
      passageMt: round(acc.passage),
      dailyMt: daily(acc.total, fuelHours.total),
      dailyByState: Object.fromEntries(
        VOYAGE_STATES.map((s) => [s, daily(acc.byState[s], fuelHours[s])]),
      ) as Record<VoyageState, number | null>,
      seaKgPerNm: perNm(seaMt, seaDistance),
      reconciliation: {
        openingMt: acc.opening,
        bunkeredMt: round(acc.bunkered),
        closingMt: acc.closing,
        robConsumptionMt:
          acc.opening === null || acc.closing === null
            ? null
            : round(acc.opening + acc.bunkered - acc.closing),
        reportedMt: round(acc.reported),
        differenceMt,
        issue: issueOf(differenceMt),
      },
    };
  });
  const seaMt = fuelRows.reduce((sum, row) => sum + row.byState.sea + row.byState.waiting, 0);

  return {
    summary,
    fuelHours,
    fuels: fuelRows,
    totalMt: summary.consumptionMt,
    dailyMt: summary.consumptionMt === null ? null : daily(summary.consumptionMt, fuelHours.total),
    seaKgPerNm: summary.consumptionMt === null ? null : perNm(seaMt, seaDistance),
    periods: summary.periods.map((period, index) => {
      const acc = periods[index] as SpanAcc;
      const totalMt = totalOf(acc.fuels);
      return {
        ...period,
        fuels: roundMap(acc.fuels),
        totalMt,
        fuelHours: round(acc.hours),
        dailyMt: totalMt === null ? null : daily(totalMt, acc.hours),
      };
    }),
    legs: summary.legs.map((leg, index) => {
      const acc = legs[index] as SpanAcc;
      const totalMt = totalOf(acc.fuels);
      return {
        ...leg,
        fuels: roundMap(acc.fuels),
        totalMt,
        fuelHours: round(acc.hours),
        dailyMt: totalMt === null ? null : daily(totalMt, acc.hours),
        kgPerNm: totalMt === null ? null : perNm(totalMt, leg.distanceNm),
      };
    }),
    robFlow,
    issues: robFlow.filter((row) => row.issue !== null).length,
  };
}
